import { flatten } from "flat"
import type { Transaction } from "@/model/DataModel"
import { TransactionAreaField, transactionAreaValue } from "@/utils/transactionUnitFields"
import { AreaUnit } from "@/utils/unitSystem"

type CsvTransaction = Record<string, unknown>

const areaColumns = [
	{
		field: TransactionAreaField.TotalLandArea,
		squareFeet: "superficy.squareFeet",
		squareMeters: "superficy.squareMeters",
	},
	{
		field: TransactionAreaField.BuildableArea,
		squareFeet: "buildableAreaSqft",
		squareMeters: "buildableAreaSquareMeters",
	},
	{
		field: TransactionAreaField.UnitArea,
		squareFeet: "unitAreaSqft",
		squareMeters: "unitAreaSquareMeters",
	},
	{
		field: TransactionAreaField.DevelopableLandArea,
		squareFeet: "developableLandAreaSqft",
		squareMeters: "developableLandAreaSquareMeters",
	},
	{
		field: TransactionAreaField.NetLandArea,
		squareFeet: "netLandAreaSqft",
		squareMeters: "netLandAreaSquareMeters",
	},
] as const

const columns = [
	"presentationID",
	"inscriptionNumber",
	"deedOfSaleDate",
	"location.address",
	"location.municipality",
	"location.parcelNumbers",
	"superficy.squareFeet",
	"salePrice",
	"seller.name",
	"buyer.name",
	"inscriptionNumber",
	"psaDate",
	"assetType",
	"landCategory",
	"landSubcategory",
	"saleContext",
	"landSubtype",
	"authorizedUses",
	"currentState",
	"tod",
	"zoningPlanLink",
	"zoningGridLink",
	"urbanPlanLink",
	"padLink",
	"codificationType",
	"projectApproval",
	"projectApprovalAtDeedOfSale",
	"projectDocument",
	"projectLink",
	"project3dModelLink",
	"stories",
	"siteCoverageRatio",
	"density",
	"buildableAreaSqft",
	"numberOfUnits",
	"parkingOutdoorCount",
	"parkingIndoorCount",
	"parkingTotal",
	"parkingRatio",
	"projectComments",
	"unitType",
	"unitAreaSqft",
	"cptaq",
	"urbanPerimeter",
	"infrastructure",
	"landShape",
	"parcelReport",
	"contamination",
	"contaminationCost",
	"wetlands",
	"floodableZone",
	"endangeredSpecies",
	"deductionRatio",
	"deductionComments",
	"deductionFiles",
	"developableLandAreaSqft",
	"infrastructureDeductionRatio",
	"infrastructureDeductionComments",
	"netLandAreaSqft",
	"dataRoomLink",
]

const sensitiveColumns = new Set(["projectComments"])

function areaColumn(column: (typeof areaColumns)[number], areaUnit: AreaUnit): string {
	return areaUnit === AreaUnit.SquareMeters ? column.squareMeters : column.squareFeet
}

export function createTransactionCsv(
	transactions: Transaction[],
	areaUnit: AreaUnit,
	canReadSensitiveFields: boolean
): { data: CsvTransaction[]; columns: string[] } {
	const areaColumnBySquareFeet = new Map<string, string>(
		areaColumns.map(column => [column.squareFeet, areaColumn(column, areaUnit)])
	)
	const preferredColumns = columns
		.filter(column => canReadSensitiveFields || !sensitiveColumns.has(column))
		.map(column => areaColumnBySquareFeet.get(column) ?? column)
	const data = transactions.map(transaction => {
		const csvTransaction = flatten(transaction, { safe: true }) as CsvTransaction
		csvTransaction["documentNumber"] = csvTransaction["id"]
		delete csvTransaction["id"]
		csvTransaction["parcels"] = transaction.parcels?.filter(Boolean).map(parcel => parcel?.parcelID) ?? []

		for (const column of areaColumns) {
			delete csvTransaction[column.squareFeet]
			delete csvTransaction[column.squareMeters]
			csvTransaction[areaColumn(column, areaUnit)] = transactionAreaValue(transaction, column.field, areaUnit)
		}

		return csvTransaction
	})

	return { data, columns: preferredColumns }
}
